import type { PlayerProfile } from '../profile/schema';
import { levelForXp, resolveSpecies, xpToNextLevel, type SimState } from '../sim/index';
import type { VoyageView } from '../voyage/index';
import { CHARTED_PLACES } from '../voyage/places';
import { catchModelAt, type CatchPresentation } from './catch-presentation';
import type { CatchFacts, CatchInstrumentState } from './catch-instrument';
import { currentGoal, destinationResult, firstAtlasState, type DestinationResult, type GoalView } from './goals';
import { APP_ACTION_KINDS, type AppAction } from './actions';
import { type FisherIdentity } from './outfitter';
import { harbourWallet } from './harbour';

/**
 * Ephemeral state is deliberately supplied by the shell and never serialized: the catch under
 * way, the instant Reel was pressed (or null), and the facts the plate prints.
 */
export type PresentationState = CatchInstrumentState;
/**
 * A complete read model for the UI. It contains settled authority only; consumers do
 * not need to reach back into a session, wall clock, or simulator to fill an overlay.
 */
export interface AppPresentation {
  readonly nowMs: number;
  readonly goal: GoalView;
  readonly firstAtlas: ReturnType<typeof firstAtlasState>;
  /** Task 3's catch model with Reel applied from the instant it was pressed. */
  readonly catch: CatchPresentation;
  /** What the plate prints for `catch`, or null between catches. */
  readonly catchFacts: CatchFacts | null;
  readonly phase: VoyageView['fix']['phase'];
  readonly voyage: Pick<VoyageView, 'fix' | 'chart' | 'hold' | 'order'>;
  readonly profile: Pick<PlayerProfile, 'settings' | 'tutorial' | 'chosenDestinations' | 'arrivedDestinations' | 'arrivalResults' | 'arrivalTargets' | 'milestones' | 'letters' | 'endingSeen' | 'targetPaidTicks'>;
  readonly destinations: Readonly<Record<'murrin-nearshore' | 'murrin-outer-shelf', DestinationResult>>;
  readonly player: {
    readonly level: number;
    readonly xp: number;
    readonly xpToNextLevel: number;
    readonly identity: FisherIdentity;
    readonly coins: number;
    readonly catches: SimState['catches'];
    readonly records: SimState['records'];
    readonly mastery: SimState['mastery'];
    readonly quarry: { readonly id: string; readonly label: string; readonly selected: boolean };
  };
  readonly tutorial: { readonly mode: PlayerProfile['tutorial']; readonly stepId: string | null };
  readonly availableActionKinds: readonly AppAction['kind'][];
  readonly voyageDestination: {
    readonly id: string | null;
    readonly label: string | null;
    readonly purpose: 'quarry' | 'survey' | 'charted' | null;
    readonly status: VoyageView['fix']['phase'];
    readonly etaSeconds: number | null;
  };
  readonly localDestinations: Readonly<Record<'murrin-nearshore' | 'murrin-outer-shelf', DestinationResult & {
    readonly selected: boolean;
    readonly current: boolean;
    readonly arrived: boolean;
    readonly resultId: string | null;
    readonly targetAtArrival: string | null;
  }>>;
}
/** @deprecated Kept as a source-compatible name for Task 4 consumers. */
export type Presentation = AppPresentation;
/** Pure selector: callers supply the settled state and timestamp. */
export function buildPresentation(nowMs: number, state: SimState, view: VoyageView, profile: PlayerProfile, ephemeral: PresentationState): AppPresentation {
  const nearshore = destinationResult('murrin-nearshore', state);
  const shelf = destinationResult('murrin-outer-shelf', state);
  // Both are total for a settled SimState; retain the explicit guard so this selector
  // never invents a UI-only fallback if the destination table is changed later.
  if (nearshore === null || shelf === null) throw new Error('missing-Murrin-destination-result');
  const quarry = resolveSpecies(state, levelForXp(state.xp));
  const currentDestinationId = view.fix.journey?.destinationPlaceId ?? view.fix.mooredAt ?? (view.order.kind === 'sail' ? view.order.to : null);
  const local = view.chart.destinations.find(destination => destination.id === currentDestinationId);
  const charted = view.chart.discovered.find(destination => destination.id === currentDestinationId);
  // A physical coast hold can precede its sight-distance gate. Name the occupied
  // harbour without adding it to the discovered chart or changing navigation.
  const harbour = view.fix.mooredAt == null ? undefined : CHARTED_PLACES.find(place => place.id === view.fix.mooredAt);
  const tutorialStep = profile.tutorial.kind === 'active' || profile.tutorial.kind === 'replay' ? profile.tutorial.stepId : null;
  const localPresentation = (id: 'murrin-nearshore' | 'murrin-outer-shelf', result: DestinationResult) => ({
    ...result,
    selected: profile.chosenDestinations.includes(id), current: currentDestinationId === id, arrived: profile.arrivedDestinations.includes(id),
    resultId: profile.arrivalResults[id] ?? null, targetAtArrival: profile.arrivalTargets[id] ?? null,
  });
  return {
    nowMs,
    goal: currentGoal(profile, state, view),
    firstAtlas: firstAtlasState(profile),
    catch: catchModelAt(nowMs, ephemeral, profile.settings),
    catchFacts: ephemeral.catch === null ? null : ephemeral.facts,
    phase: view.fix.phase,
    voyage: { fix: view.fix, chart: view.chart, hold: view.hold, order: view.order },
    profile: {
      settings: profile.settings, tutorial: profile.tutorial, chosenDestinations: profile.chosenDestinations, arrivedDestinations: profile.arrivedDestinations,
      arrivalResults: profile.arrivalResults, arrivalTargets: profile.arrivalTargets, milestones: profile.milestones, letters: profile.letters, endingSeen: profile.endingSeen, targetPaidTicks: profile.targetPaidTicks,
    },
    destinations: { 'murrin-nearshore': nearshore, 'murrin-outer-shelf': shelf },
    player: { level: levelForXp(state.xp), xp: state.xp, xpToNextLevel: xpToNextLevel(state.xp), identity: profile.identity, coins: harbourWallet(state.coins, profile.harbour, profile.outfitter.spentCoins), catches: state.catches, records: state.records, mastery: state.mastery, quarry: { id: quarry.id, label: quarry.name, selected: state.targetSpecies !== '' } },
    tutorial: { mode: profile.tutorial, stepId: tutorialStep },
    availableActionKinds: APP_ACTION_KINDS,
    voyageDestination: {
      id: currentDestinationId, label: local?.label ?? charted?.label ?? harbour?.label ?? null, purpose: local?.purpose ?? (charted === undefined ? null : 'charted'), status: view.fix.phase,
      etaSeconds: view.fix.journey === null ? view.fix.phase === 'moored' ? 0 : null : Math.max(0, view.fix.journey.durationMs - view.fix.journey.elapsedMs) / 1000,
    },
    localDestinations: { 'murrin-nearshore': localPresentation('murrin-nearshore', nearshore), 'murrin-outer-shelf': localPresentation('murrin-outer-shelf', shelf) },
  };
}
